import type { ReactNode } from "react"
import { User, Bot, Link2 } from "lucide-react"
import type {
  ChatTranscriptDetail,
  ChatTranscriptMessage,
  ChatTranscriptOutbound,
  ChatTranscriptOutboundStatus,
} from "@/lib/api"
import { cn } from "@/lib/utils"

export type TranscriptView = "member" | "audit"

type TimelineItem =
  | { at: string | null; message: ChatTranscriptMessage; unlogged: boolean }
  | { at: string | null; outbound: ChatTranscriptOutbound }

const STATUS_LABEL: Record<ChatTranscriptOutboundStatus, string | null> = {
  sent: null,
  held: "Held, never sent",
  failed: "Never delivered",
  unconfirmed: "Delivery unconfirmed",
}

function sourcedIds(outbound: ChatTranscriptOutbound[]): Set<string> {
  return new Set(outbound.map((o) => o.source_message_id).filter((id): id is string => !!id))
}

export function transcriptTimeline(d: ChatTranscriptDetail, view: TranscriptView): TimelineItem[] {
  const outbound = d.outbound ?? []
  const logStart = outbound[0]?.sent_at
  const sourced = sourcedIds(outbound)
  const messages: TimelineItem[] = d.messages
    .filter((m) => view === "audit" || m.role === "user" || !sourced.has(m.id))
    .map((m) => ({
      at: m.created_at,
      message: m,
      unlogged: m.role !== "user" && !sourced.has(m.id) && !!logStart && (m.created_at ?? "") >= logStart,
    }))
  const sends: TimelineItem[] = outbound
    .filter((o) => view === "audit" || o.status === "sent")
    .map((o) => ({ at: o.sent_at, outbound: o }))
  return [...messages, ...sends].sort((a, b) => (a.at ?? "").localeCompare(b.at ?? ""))
}

function LinkCard({ href }: { href: string }) {
  let host = href
  let path = ""
  try {
    const u = new URL(href)
    if (u.protocol !== "https:" && u.protocol !== "http:") throw new Error("scheme")
    host = u.host
    path = `${u.pathname}${u.search}${u.hash}`.replace(/^\/$/, "")
  } catch {
    return <span className="break-all">{href}</span>
  }
  return (
    <a href={href} target="_blank" rel="noreferrer"
      className="my-0.5 flex max-w-sm items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 no-underline hover:bg-muted">
      <Link2 className="size-4 shrink-0 text-blue-600" />
      <span className="min-w-0">
        <span className="block truncate text-xs font-medium">{host}</span>
        {path && <span className="block truncate text-xs text-blue-600">{path}</span>}
      </span>
    </a>
  )
}

function OutboundBody({ o }: { o: ChatTranscriptOutbound }) {
  if (o.kind === "poll") {
    const options = (o.detail.options as string[] | undefined) ?? []
    return <div>Poll: {options.join(" / ")}</div>
  }
  if (o.kind === "reaction") {
    const emoji = (o.detail.custom_emoji as string | null) || String(o.detail.reaction_type ?? "")
    return <div className="italic text-muted-foreground">Tapback {emoji} on a message</div>
  }
  if (o.kind === "contact_card") {
    return <div className="italic text-muted-foreground">Shared the Offseason contact card</div>
  }
  return (
    <>
      {o.parts.map((p, i) =>
        p.type === "link" && p.value ? (
          <LinkCard key={i} href={p.value} />
        ) : p.type === "text" ? (
          <div key={i}>{p.value}</div>
        ) : p.type === "media" && p.url ? (
          <a key={i} href={p.url} target="_blank" rel="noreferrer" className="block italic text-blue-600 underline">
            Picture sent (link may have expired)
          </a>
        ) : p.type === "media" ? (
          <div key={i} className="italic text-muted-foreground">File sent</div>
        ) : (
          <div key={i} className="italic text-muted-foreground">[{p.type}] {p.value}</div>
        ),
      )}
    </>
  )
}

function StatusMark({ label, reason }: { label: string; reason?: string | null }) {
  return (
    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
      {label}{reason ? ` (${reason})` : ""}
    </span>
  )
}

function Row({ who, isUser, at, mark, children }: {
  who: string
  isUser: boolean
  at: string | null
  mark?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex gap-3">
      <div className={cn(
        "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full",
        isUser ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700",
      )}>
        {isUser ? <User className="size-3.5" /> : <Bot className="size-3.5" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-semibold">{who}</span>
          <span className="text-[10px] text-muted-foreground">{fmtAt(at)}</span>
          {mark}
        </div>
        <div className="mt-0.5 whitespace-pre-wrap wrap-break-word text-sm">{children}</div>
      </div>
    </div>
  )
}

function fmtAt(iso: string | null): string {
  if (!iso) return "-"
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

export function TranscriptTimeline({ items, memberName, view }: {
  items: TimelineItem[]
  memberName: string
  view: TranscriptView
}) {
  return (
    <>
      {items.map((item) => {
        if ("outbound" in item) {
          const o = item.outbound
          const label = STATUS_LABEL[o.status]
          return (
            <Row key={`o-${o.id}`} who={view === "audit" ? "Coach (iMessage)" : "Coach"} isUser={false} at={o.sent_at}
              mark={label ? <StatusMark label={label} reason={o.hold_reason} /> : undefined}>
              <OutboundBody o={o} />
            </Row>
          )
        }
        const m = item.message
        const isUser = m.role === "user"
        return (
          <Row key={`m-${m.id}`} who={isUser ? memberName : view === "audit" ? "Coach (transcript)" : "Coach"}
            isUser={isUser} at={m.created_at}
            mark={item.unlogged ? <StatusMark label="No iMessage send on record" /> : undefined}>
            {m.content || <em className="text-muted-foreground">(empty)</em>}
          </Row>
        )
      })}
    </>
  )
}
